import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Modal,
  Typography,
  useTheme,
} from '@mui/material'
import { ProductDto, ProductTypeDto, RoomTypeDto } from 'api'
import cx from 'classnames'
import { ContainedButton } from 'components/contained-button'
import { HeaderLogo } from 'components/header-logo'
import { LinkDefault } from 'components/link-default'
import { BaseLink } from 'components/link-default/link-default-view'
import { PageTitle } from 'components/page-title'
import { PageWrapper } from 'components/page-wraper'
import { useFormatMessage } from 'localization'
import { PriceDisplay } from 'pages/addon-selector-page/components/price-display'
import { useCustomer, useFinishSelection, useProject } from 'queries/queries'
import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Colors } from 'styles/colors'
import { getProjectId } from 'utilities/get-project-id'
import useDimensions from 'utilities/use-dimensions'
import { useFormatPrice } from 'utilities/use-format-price'
import { useNavigateProject } from 'utilities/use-navigate-project'
import { useStaticCallback } from 'utilities/use-static-callback'
import { AddonSelectorContext } from '../../contexts/addon-selector-context'

const useSummaryElement = (rooms: RoomWithPrice[]): [string | null, JSX.Element] => {
  const f = useFormatMessage()
  const formatPrice = useFormatPrice()
  const { productsSum, hasOfferedPrice } = AddonSelectorContext.useState()
  const ref = React.useRef<HTMLTableElement>(null)
  const [summaryHtml, setSummaryHtml] = React.useState<string | null>(null)
  React.useEffect(() => {
    if (ref.current === null) return
    setSummaryHtml(ref.current.innerHTML.replace(/class=".*?"/g, ''))
  }, [])

  return [
    summaryHtml,
    <table
      key={1}
      ref={ref}
      style={{
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'left',
        borderCollapse: 'collapse',
      }}>
      {rooms.map((room) => {
        return (
          <React.Fragment key={room.id}>
            <thead>
              <tr>
                <th colSpan={3} style={{ borderBottom: '1px solid lightgray' }}>
                  <Typography variant="h6" style={{ fontSize: '18px', fontWeight: 300 }}>
                    {room.name}
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {room.productTypes.map((productType, index) => {
                const offeredPrice = productType.selectedProduct?.price === -1
                return (
                  <tr
                    key={productType.id}
                    style={index % 2 ? undefined : { backgroundColor: '#f5f5f5' }}>
                    <td style={{ borderRight: '1px solid lightgray' }}>
                      <Typography
                        color={offeredPrice ? 'error' : 'inherit'}
                        variant="body1"
                        style={{ marginRight: '10px', fontSize: '14px', fontWeight: 300 }}>
                        {productType.name}
                      </Typography>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Typography
                        color={offeredPrice ? 'error' : 'inherit'}
                        variant="body1"
                        style={{
                          marginLeft: '10px',
                          marginRight: '10px',
                          fontSize: '14px',
                          fontWeight: 300,
                        }}>
                        {productType.selectedProduct?.name ?? 'Standard'}
                      </Typography>
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        borderLeft: '1px solid lightgray',
                      }}>
                      <Typography
                        color={offeredPrice ? 'error' : 'inherit'}
                        variant="body2"
                        style={{ marginLeft: '10px', fontSize: '14px', fontWeight: 300 }}>
                        {formatPrice(productType.selectedProduct?.price ?? 0)}
                      </Typography>
                    </td>
                  </tr>
                )
              })}

              <tr>
                <td
                  style={{
                    borderRight: '1px solid lightgray',
                    borderTop: '1px solid lightgray',
                  }}></td>
                <td style={{ padding: '12px', borderTop: '1px solid lightgray' }}>
                  <Typography
                    variant="subtitle1"
                    style={{
                      marginLeft: '10px',
                      marginRight: '10px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}>
                    {f('SUMMARY_PAGE_TOTAL_LABEL')}
                  </Typography>
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    borderLeft: '1px solid lightgray',
                    borderTop: '1px solid lightgray',
                  }}>
                  <Typography
                    variant="subtitle2"
                    style={{ marginLeft: '10px', fontSize: '14px', fontWeight: 'bold' }}>
                    {formatPrice(room.roomPrice)}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </React.Fragment>
        )
      })}
      <tr>
        <td style={{ paddingTop: '32px' }}>
          <Typography variant="overline" style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>
            {f('PRICE_DISPLAY_CAPTION_LABEL')}
            {hasOfferedPrice && (
              <Typography
                variant="overline"
                style={{ whiteSpace: 'nowrap', fontWeight: 700, color: Colors.Contessa }}>
                {` (${f('USE_FORMAT_TOTAL_PRICE_LABEL')})`}
              </Typography>
            )}
          </Typography>
          <Typography variant="h3" style={{ fontWeight: 'bold' }}>
            {formatPrice(productsSum ?? 0)}
          </Typography>
        </td>
      </tr>
    </table>,
  ]
}

type ProductWithOption = ProductTypeDto & { selectedProduct?: ProductDto }

type RoomWithPrice = Omit<RoomTypeDto, 'productTypes'> & {
  productTypes: ProductWithOption[]
  roomPrice: number
}

const SummaryPageView = () => {
  const { apartmentType } = AddonSelectorContext.useState()
  const { getSelectedProductFor, refetchSelections } = AddonSelectorContext.useActions()

  const projectId = getProjectId()
  const { data: project } = useProject({ projectId })
  const { data: customer } = useCustomer({ projectId })

  const { isLoading, mutateAsync, error, reset } = useFinishSelection({
    projectId,
    customerId: customer?.id,
  })

  const formatPrice = useFormatPrice()
  const f = useFormatMessage()
  const navigate = useNavigateProject()
  const theme = useTheme()

  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = React.useState(false)

  const [open, setOpen] = React.useState(false)
  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    reset()
  }

  const roomsWithPrice: RoomWithPrice[] = React.useMemo(() => {
    return apartmentType.roomTypes.map((roomType) => {
      const productTypes: ProductWithOption[] = roomType.productTypes.map((productType) => {
        const selectedProduct = getSelectedProductFor({ productType, roomType })
        return { ...productType, selectedProduct }
      })
      const roomPrice = productTypes
        .map((product) => product.selectedProduct?.price ?? 0)
        .filter((price) => price !== -1)
        .reduce((acc, price) => acc + price, 0)
      return { ...roomType, productTypes, roomPrice }
    })
  }, [apartmentType.roomTypes, getSelectedProductFor])

  const [summaryHtml, mountMe] = useSummaryElement(roomsWithPrice)

  const submit = useStaticCallback(() => {
    mutateAsync({
      logotypeUrl: project?.logotypeUrl ?? '',
      summary: summaryHtml,
      body: project?.emailText ?? '',
      info: `${
        customer?.apartmentType
          ? `<tr><td align="center"><p style="font-size: 14px; font-weight: 300;">${f(
              'SUMMARY_PAGE_APARTMENT_TYPE'
            )} ${customer?.apartmentType}</p></td></tr>`
          : ''
      }${
        customer?.apartmentNumber
          ? `<tr><td align="center"><p style="font-size: 14px; font-weight: 300;">${f(
              'SUMMARY_PAGE_APARTMENT_NUMBER'
            )} ${customer?.apartmentNumber}</p></td></tr>`
          : ''
      }`,
      title: project?.name ?? '',
      primaryColor: theme.palette.primary.main,
      primaryContrastColor: theme.palette.primary.contrastText,
      primaryTextColor: theme.palette.text.primary,
    }).then(() => navigate(`/finish`))
  })

  useEffect(() => {
    refetchSelections()
  }, [refetchSelections])

  /* Fix for iOS Safari squish content on orientation change */
  const dimensions = useDimensions()

  return (
    <PageWrapper
      className="flex-shrink-0 !px-0"
      topBarComponent={
        <div className="px-4 border-b border-lightgray">
          <HeaderLogo displayLogout={true} size="small" />
          <PageTitle title={f('SUMMARY_PAGE_TITLE')} className={'text-center uppercase'} />
        </div>
      }
      bottomBarComponent={
        <>
          <div className="h-52 md:h-32" />
          <div className="absolute left-0 right-0 bottom-0 h-52 md:h-32 px-4 md:px-0 border-t border-lightgray">
            <div className="flex flex-row justify-between">
              <PriceDisplay summaryView={true} />
              {!!error && !project?.termsAndConditionsUrl && (
                <Typography variant="body1" color="error" className="bold">
                  {error as string}
                </Typography>
              )}
            </div>
            <div className="flex flex-col-reverse justify-between sm:flex-row my-2">
              <ContainedButton color="secondary" className="mb-4" onClick={() => navigate(-1)}>
                {f('SUMMARY_PAGE_SECONDARY_ACTION_LABEL')}
              </ContainedButton>
              <div className="h-4 w-4" />
              <ContainedButton
                color="primary"
                onClick={!project?.termsAndConditionsUrl ? submit : handleOpen}
                showSpinner={isLoading}>
                {f('SUMMARY_PAGE_PRIMARY_ACTION_LABEL')}
              </ContainedButton>
            </div>
          </div>
        </>
      }>
      <div className="flex-1 h-[1px] overflow-x-auto w-full">
        <table>
          {roomsWithPrice.map((room) => {
            return (
              <React.Fragment key={room.id}>
                <thead>
                  <tr>
                    <th colSpan={3}>
                      <Typography variant="h6" className="pl-4 text-left">
                        {room.name}
                      </Typography>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {room.productTypes.map((productType, index) => {
                    const offeredPrice = productType.selectedProduct?.price === -1
                    return (
                      <tr
                        key={(productType.id, index)}
                        className={cx('py-2 border-y', index % 2 === 0 && 'bg-lightgray')}>
                        <td className="w-4/12">
                          <Typography
                            color={offeredPrice ? 'error' : 'inherit'}
                            variant="body1"
                            className="pl-4 text-sm">
                            {productType.name}
                          </Typography>
                        </td>
                        <td className="w-4/12 px-2 py-2 border-x">
                          <Typography
                            color={offeredPrice ? 'error' : 'inherit'}
                            variant="body1"
                            className="text-sm">
                            {productType.selectedProduct?.name ?? 'Standard'}
                          </Typography>
                        </td>
                        <td className="text-sm text-right flex-nowrap w-4/12 py-2">
                          <Typography
                            color={offeredPrice ? 'error' : 'inherit'}
                            variant="body2"
                            className="pr-4">
                            {formatPrice(productType.selectedProduct?.price ?? 0)}
                          </Typography>
                        </td>
                      </tr>
                    )
                  })}

                  <tr>
                    <td></td>
                    <td>
                      <Typography variant="subtitle1" className="text-sm m-2">
                        {f('SUMMARY_PAGE_TOTAL_LABEL')}
                      </Typography>
                    </td>
                    <td className="pr-4 text-sm text-right whitespace-nowrap">
                      <Typography variant="subtitle2">{formatPrice(room.roomPrice)}</Typography>
                    </td>
                  </tr>
                </tbody>
              </React.Fragment>
            )
          })}
        </table>
        <div className="hidden">
          <p>width: {dimensions.width}</p>
          <p>height: {dimensions.height}</p>
        </div>
      </div>
      <Modal open={open} onClose={handleClose} aria-labelledby="Terms-and-conditions-modal">
        <div className="bg-primary-contrastText w-[80%] max-w-[400px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] outline-none border-0 p-5">
          <Typography variant="h5" className="mb-4">
            {f('SUMMARY_PAGE_ABPROVE_TERMS_AND_CONDITIONS_TITLE')}
          </Typography>
          <FormControlLabel
            color="primary"
            checked={acceptTermsAndConditions}
            onChange={(e, checked) => setAcceptTermsAndConditions(checked)}
            control={<Checkbox color="primary" />}
            label={
              <Typography variant="body2">
                <FormattedMessage
                  id="SUMMARY_PAGE_ABPROVE_TERMS_AND_CONDITIONS"
                  values={{
                    link: (chunks) => (
                      <TermsAndConditionsLink url={project?.termsAndConditionsUrl}>
                        {chunks}
                      </TermsAndConditionsLink>
                    ),
                  }}
                />
              </Typography>
            }
          />
          {error ? <FormHelperText error>{error as string}</FormHelperText> : null}
          <ContainedButton
            color="primary"
            onClick={submit}
            className="ml-auto mt-4 w-32"
            disabled={!acceptTermsAndConditions || customer === undefined}
            showSpinner={isLoading}>
            {f('SUMMARY_PAGE_ABPROVE_TERMS_AND_CONDITIONS_BUTTON')}
          </ContainedButton>
        </div>
      </Modal>
      <div className="hidden">{mountMe}</div>
    </PageWrapper>
  )
}

const TermsAndConditionsLink: React.FC<React.PropsWithChildren<{ url?: string | null }>> = ({
  children,
  url,
}) => {
  const [error, setError] = React.useState<string | null>(null)
  if (error) {
    throw Error(error)
  }

  if (!url) {
    return (
      <BaseLink onClick={() => setError('No provided link found for terms and conditions')}>
        {children}
      </BaseLink>
    )
  }
  return (
    <LinkDefault external href={url} target="_blank" rel="noopener noreferrer">
      {children}
    </LinkDefault>
  )
}

export default SummaryPageView
